import type { DailyReportMetric, DailyReportRow } from 'api/generated'
import {
  type Granularity,
  axisProps,
  formatMetric,
  formatRowDate,
} from 'helpers/daily-report'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

type Point = { date: string; value: number | null }

const toPoints = (rows: DailyReportRow[], metric: DailyReportMetric) =>
  rows.map((row) => ({ date: row.date, value: row.values[metric.key] ?? null }))

const PointTooltip = ({
  active,
  payload,
  metric,
  granularity,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
  metric: DailyReportMetric
  granularity: Granularity
}) => {
  const point = payload?.[0]?.payload as Point | undefined
  if (!active || !point) return null
  return (
    <div className='rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm'>
      <div className='text-muted-foreground'>
        {formatRowDate(point.date, granularity)}
      </div>
      <div className='font-semibold tabular-nums'>
        {formatMetric(metric, point.value)}
      </div>
    </div>
  )
}

/**
 * One metric over time: a line (or bars for `sum` metrics on coarse rows, e.g.
 * revenue per month). Single series, so no legend: the card title names it.
 */
export const MetricTrendChart = ({
  rows,
  metric,
  granularity,
  bars = false,
  height = 256,
}: {
  rows: DailyReportRow[]
  metric: DailyReportMetric
  granularity: Granularity
  bars?: boolean
  height?: number
}) => {
  const data = toPoints(rows, metric)
  const xAxis = (
    <XAxis
      dataKey='date'
      tickFormatter={(d: string) =>
        formatRowDate(d, granularity).replace('Sett. ', '')
      }
      minTickGap={24}
      {...axisProps}
    />
  )
  const yAxis = (
    <YAxis
      tickFormatter={(v: number) => formatMetric(metric, v)}
      width={80}
      // Totals move little day to day: a zero baseline flattens them
      domain={bars ? [0, 'auto'] : ['auto', 'auto']}
      {...axisProps}
    />
  )
  const grid = (
    <CartesianGrid
      vertical={false}
      stroke='var(--border)'
      strokeDasharray='3 3'
    />
  )
  const tooltip = (
    <Tooltip
      cursor={
        bars ? { fill: 'var(--muted)' } : { stroke: 'var(--muted-foreground)' }
      }
      content={({ active, payload }) => (
        <PointTooltip
          active={active}
          payload={payload}
          metric={metric}
          granularity={granularity}
        />
      )}
    />
  )

  return (
    <div style={{ height }}>
      <ResponsiveContainer width='100%' height='100%'>
        {bars ? (
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            <Bar
              dataKey='value'
              fill='var(--primary)'
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
              isAnimationActive={false}
            />
          </BarChart>
        ) : (
          <LineChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            <Line
              type='monotone'
              dataKey='value'
              stroke='var(--primary)'
              strokeWidth={2}
              dot={data.length === 1}
              activeDot={{ r: 4 }}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}

/** Axis-less trend for the channel cards, with the same hover tooltip. */
export const Sparkline = ({
  rows,
  metric,
  granularity,
}: {
  rows: DailyReportRow[]
  metric: DailyReportMetric
  granularity: Granularity
}) => (
  <div className='h-12'>
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart
        data={toPoints(rows, metric)}
        margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
      >
        <YAxis hide domain={['auto', 'auto']} />
        <Tooltip
          cursor={{ stroke: 'var(--muted-foreground)' }}
          content={({ active, payload }) => (
            <PointTooltip
              active={active}
              payload={payload}
              metric={metric}
              granularity={granularity}
            />
          )}
        />
        <Line
          type='monotone'
          dataKey='value'
          stroke='var(--primary)'
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 3 }}
          connectNulls
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
)
